import { Alert, Button, Checkbox, Fieldset, Group, Radio, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMicrophone, IconPlayerStopFilled, IconSparkles } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  InventoryItemForm,
  InventoryItemFormErrors,
} from 'src/Components/Forms/Inventory/InventoryItemForm';
import { ChosenInvoice } from 'src/Components/Inventory/InventoryInvoices';
import { InventoryFormValues } from 'src/Components/Inventory/InventoryItemFields';
import { formatEuros } from 'src/Components/Inventory/inventoryLabels';
import { useInventoryRooms } from 'src/Components/Inventory/useInventoryRooms';
import { TextInput } from 'src/Components/Shared/TextInput';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { useCreateInventoryItem } from 'src/Hooks/Mutations/useInventoryMutations';
import useFetchInventory from 'src/Hooks/Queries/useFetchInventory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import {
  isVoiceRecordingSupported,
  useVoiceRecorder,
  VoiceRecording,
} from 'src/Hooks/Shared/useVoiceRecorder';
import { inventoryApi, memoriesApi } from 'src/Services/API';
import {
  InventoryCreatedDto,
  InventoryDraftDto,
  InventoryValueSource,
  LinkableEntityType,
} from 'src/Services/API/generated';
import { MemoryDatePrecision } from 'src/Services/API/Memories.api';
import { trackInventoryItemCreated } from 'src/Services/Mixpanel/events';
import { formatMemoryDate } from 'src/Utils/memoryDate';

import styles from './InventoryItemCreationFormWrapper.module.scss';
import { Props } from './InventoryItemCreationFormWrapper.types';

// The whole story, all parts together (« Continuer à raconter »).
const VOICE_MAX_SECONDS = 180;

const EMPTY_VALUES: InventoryFormValues = {
  name: '',
  roomId: null,
  brandAndModel: '',
  condition: '',
  notes: '',
  quantity: 1,
  purchaseYear: null,
  purchasePrice: null,
  replacementValue: null,
  replacementValueSource: null,
  replacementValueSourceLabel: null,
  replacementValueSourceUrl: null,
  depreciationEnabled: true,
};

const suggestionKey = (type: string, id: string) => `${type}:${id}`;

const formatClock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * Add an object in one screen: photos, optionally a few words for Lucien who fills the card,
 * then the card itself and its invoices.
 */
const InventoryItemCreationFormWrapper = ({ onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const assetId = useCurrentAssetId();
  const [searchParams] = useSearchParams();
  const { data: inventory } = useFetchInventory();
  const { roomOptions, createRoom, isCreatingRoom } = useInventoryRooms();
  const { mutateAsync: createItem } = useCreateInventoryItem();

  const [values, setValues] = useState<InventoryFormValues>({
    ...EMPTY_VALUES,
    roomId: searchParams.get('roomId'),
  });
  const [photos, setPhotos] = useState<FormAttachment[]>([]);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [invoices, setInvoices] = useState<ChosenInvoice[]>([]);
  // The member may stop and go on telling: each part is kept, in order.
  const [recordings, setRecordings] = useState<VoiceRecording[]>([]);
  const [draft, setDraft] = useState<InventoryDraftDto | null>(null);
  const [isDrafting, setIsDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [lucienHint, setLucienHint] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [taskTitle, setTaskTitle] = useState<string | null>(null);
  const [taskChecked, setTaskChecked] = useState(true);
  const [memoryTitle, setMemoryTitle] = useState<string | null>(null);
  const [memoryChecked, setMemoryChecked] = useState(true);
  // Lucien heard a date in the story (« offert en 1950 »): the memory is dated then, unless
  // the member says it is from today.
  const [useToldDate, setUseToldDate] = useState(true);
  const [webPriceAnswered, setWebPriceAnswered] = useState(false);
  const [errors, setErrors] = useState<InventoryItemFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const startedAt = useRef(Date.now());

  const rule = inventory?.settings ?? { depreciationRatePercent: 10, depreciationMaxPercent: 80 };
  const language = i18n.language?.startsWith('en') ? 'en' : 'fr';

  const onChange = (patch: Partial<InventoryFormValues>) =>
    setValues(current => ({ ...current, ...patch }));

  const recordedSeconds = recordings.reduce((sum, part) => sum + part.durationSeconds, 0);
  const remainingSeconds = Math.max(0, VOICE_MAX_SECONDS - recordedSeconds);
  const { isRecording, elapsedSeconds, start, stop } = useVoiceRecorder({
    maxDurationSeconds: Math.max(1, remainingSeconds),
    onRecorded: part => setRecordings(current => [...current, part]),
  });

  const startRecording = async () => {
    setLucienHint(null);
    try {
      await start();
    } catch {
      setLucienHint(
        t(
          'inventory.add.micDenied',
          'Le micro n’est pas autorisé. Autorisez-le dans le navigateur, ou remplissez la fiche à la main.'
        )
      );
    }
  };

  // ---------------------------------------------------------------------------------------
  // Lucien
  // ---------------------------------------------------------------------------------------

  const applyDraft = (result: InventoryDraftDto | null) => {
    setDraft(result);
    if (result) {
      setValues(current => ({
        ...current,
        roomId: result.roomId ?? current.roomId,
        name: result.name || current.name,
        brandAndModel: result.brandAndModel ?? current.brandAndModel,
        condition: result.condition ?? current.condition,
        quantity: result.quantity ?? current.quantity,
        purchaseYear: result.purchaseYear ?? current.purchaseYear,
        purchasePrice: result.purchasePrice ?? current.purchasePrice,
        ...(result.replacementValue !== null &&
          result.replacementValue !== undefined && {
            replacementValue: result.replacementValue,
            replacementValueSource: InventoryValueSource.Manual,
            replacementValueSourceLabel: null,
            replacementValueSourceUrl: null,
          }),
      }));
    }
    const preselected = (result?.suggestedLinks ?? []).filter(link => link.preselected);
    setChecked(new Set(preselected.map(link => suggestionKey(link.type, link.id))));
    setTaskTitle(result?.suggestedTaskTitle ?? null);
    setTaskChecked(true);
    setMemoryTitle(result?.suggestedMemoryTitle ?? null);
    setMemoryChecked(true);
    setUseToldDate(true);
    setWebPriceAnswered(false);
  };

  const askLucien = async () => {
    setLucienHint(null);
    if (isRecording) {
      setLucienHint(t('inventory.add.stopFirst', 'Appuyez d’abord sur « Arrêter ».'));
      return;
    }
    if (!photos.length) {
      setLucienHint(t('inventory.add.photoRequired', 'Prenez au moins une photo de l’objet.'));
      return;
    }
    // Photos keep uploading while the member speaks: Lucien waits for all of them.
    if (photosUploading) {
      setLucienHint(t('inventory.add.photosPending', 'Attendez que les photos soient envoyées.'));
      return;
    }
    setIsDrafting(true);
    setDraftError(null);
    let result: InventoryDraftDto | null = null;
    try {
      const photoIds = photos.flatMap(photo => photo.fileId ?? []);
      const files = recordings.map(
        part => new File([part.audio], part.filename, { type: part.audio.type })
      );
      result = (
        await inventoryApi.inventoryControllerDraft(
          assetId!,
          photoIds,
          files.length ? files : undefined,
          values.roomId ?? undefined,
          language
        )
      ).data;
    } catch {
      result = null;
    }
    if (!result?.drafted) {
      setDraftError(
        t(
          'inventory.add.draftFailed',
          "Lucien n'a pas pu préparer la fiche. Remplissez-la à la main, c'est rapide."
        )
      );
    }
    applyDraft(result);
    setIsDrafting(false);
  };

  const acceptWebPrice = () => {
    if (!draft?.suggestedReplacementValue) return;
    onChange({
      replacementValue: draft.suggestedReplacementValue ?? null,
      replacementValueSource: InventoryValueSource.Lucien,
      replacementValueSourceLabel: draft.suggestedReplacementValueLabel ?? null,
      replacementValueSourceUrl: draft.suggestedReplacementValueUrl ?? null,
    });
    setWebPriceAnswered(true);
  };

  const createSpokenRoom = async () => {
    if (!draft?.unknownRoomName) return;
    try {
      const room = await createRoom(draft.unknownRoomName);
      onChange({ roomId: room.id });
      setDraft(current => (current ? { ...current, unknownRoomName: null } : current));
    } catch {
      notifications.show({
        color: 'red',
        message: t('inventory.add.roomCreateError', "La pièce n'a pas pu être créée."),
      });
    }
  };

  const toggle = (key: string) =>
    setChecked(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const documentSuggestions = (draft?.suggestedLinks ?? []).filter(
    link => link.type === LinkableEntityType.VirtualFile
  );
  const otherSuggestions = (draft?.suggestedLinks ?? []).filter(
    link => link.type !== LinkableEntityType.VirtualFile
  );

  const toldDate = draft?.suggestedMemoryTakenAt
    ? formatMemoryDate(
        t,
        draft.suggestedMemoryTakenAt,
        draft.suggestedMemoryTakenAtPrecision as MemoryDatePrecision,
        draft.suggestedMemoryTakenAtApproximate
      )
    : null;

  // ---------------------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------------------

  const reset = (roomId: string | null) => {
    setValues({ ...EMPTY_VALUES, roomId });
    setPhotos([]);
    setInvoices([]);
    setRecordings([]);
    setDraft(null);
    setDraftError(null);
    setLucienHint(null);
    setChecked(new Set());
    setTaskTitle(null);
    setMemoryTitle(null);
    setErrors({});
    startedAt.current = Date.now();
  };

  const save = async (addAnother: boolean) => {
    const nextErrors: InventoryItemFormErrors = {};
    if (!values.name.trim()) {
      nextErrors.name = t(
        'inventory.add.nameRequired',
        "Donnez un nom à l'objet (ex. « Canapé »)."
      );
    }
    if (!photos.length) {
      nextErrors.photos = t('inventory.add.photoRequired', 'Prenez au moins une photo de l’objet.');
    } else if (photosUploading) {
      nextErrors.photos = t(
        'inventory.add.photosPending',
        'Attendez que les photos soient envoyées.'
      );
    }
    setErrors(nextErrors);
    if (nextErrors.name || nextErrors.photos) return;

    // A pre-ticked invoice-like document is attached as an invoice; others as plain links.
    const invoiceIds = new Set(invoices.map(invoice => invoice.id));
    documentSuggestions
      .filter(link => link.preselected && checked.has(suggestionKey(link.type, link.id)))
      .forEach(link => invoiceIds.add(link.id));
    const links = [...documentSuggestions.filter(link => !link.preselected), ...otherSuggestions]
      .filter(link => checked.has(suggestionKey(link.type, link.id)))
      .map(link => ({ type: link.type, id: link.id }));
    const withTask = !!taskTitle?.trim() && taskChecked;
    const withMemory = !!memoryTitle?.trim() && memoryChecked;

    setIsSaving(true);
    // Only the creation itself can fail as "not saved": what follows must never invite a
    // second click (duplicate object).
    let item: InventoryCreatedDto;
    try {
      item = await createItem({
        name: values.name.trim(),
        roomId: values.roomId,
        brandAndModel: values.brandAndModel || null,
        condition: values.condition || null,
        notes: values.notes || null,
        quantity: values.quantity,
        purchaseYear: values.purchaseYear,
        purchasePrice: values.purchasePrice,
        replacementValue: values.replacementValue,
        replacementValueSource: values.replacementValueSource,
        replacementValueSourceLabel: values.replacementValueSourceLabel,
        replacementValueSourceUrl: values.replacementValueSourceUrl,
        depreciationEnabled: values.depreciationEnabled,
        photoIds: photos.flatMap(photo => photo.fileId ?? []),
        invoiceFileIds: [...invoiceIds],
        links,
        task: withTask ? { title: taskTitle!.trim() } : undefined,
        memory: withMemory
          ? {
              title: memoryTitle!.trim(),
              description: draft?.transcript ?? undefined,
              ...(toldDate &&
                useToldDate && {
                  takenAt: draft!.suggestedMemoryTakenAt!,
                  takenAtPrecision: draft!.suggestedMemoryTakenAtPrecision,
                  takenAtApproximate: draft!.suggestedMemoryTakenAtApproximate,
                }),
            }
          : undefined,
      });
    } catch {
      setErrors({
        submit: t(
          'inventory.add.saveError',
          "L'objet n'a pas pu être enregistré. Vérifiez votre connexion et réessayez."
        ),
      });
      setIsSaving(false);
      return;
    }

    const warnings = [...item.warnings];
    // The voice is kept with the memory, as a story told by the member, part after part. A
    // single part reuses Lucien's transcript; several parts are transcribed one by one.
    if (item.createdMemoryId && recordings.length) {
      try {
        for (const part of recordings) {
          await memoriesApi.addRecording(assetId!, item.createdMemoryId, {
            audio: part.audio,
            filename: part.filename,
            durationSeconds: part.durationSeconds,
            transcript: recordings.length === 1 ? (draft?.transcript ?? undefined) : undefined,
            language,
          });
        }
      } catch {
        warnings.push('RECORDING');
      }
    }
    setIsSaving(false);

    notifications.show({
      color: 'teal',
      message: t('inventory.add.savedTitle', '{{name}} enregistré', { name: item.name }),
    });
    if (warnings.length) {
      notifications.show({
        color: 'orange',
        message: t(
          'inventory.add.partialSave',
          "L'objet est enregistré, mais l'action ou le souvenir n'a pas pu être créé. Vous pourrez le faire depuis sa fiche."
        ),
      });
    }

    trackInventoryItemCreated({
      drafted_by_lucien: !!draft?.drafted,
      has_voice: recordings.length > 0,
      voice_parts: recordings.length,
      photos_count: photos.length,
      invoices_count: invoiceIds.size,
      value_source:
        values.replacementValueSource === InventoryValueSource.Lucien
          ? 'lucien'
          : values.replacementValue != null
            ? 'manual'
            : 'none',
      created_task: !!item.createdTaskId,
      created_memory: !!item.createdMemoryId,
      seconds_spent: Math.round((Date.now() - startedAt.current) / 1000),
    });

    if (addAnother) {
      // Next object, same room: the inventory goes room by room.
      reset(item.room?.id ?? values.roomId);
    } else {
      onClose();
    }
  };

  // ---------------------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------------------

  const lucienSection = (
    <Fieldset legend={t('inventory.add.lucienTitle', 'Remplir avec Lucien')}>
      <div className={styles.section}>
        <Text size="sm" c="dimmed">
          {t(
            'inventory.add.lucienText',
            'Ajoutez les photos, dites quelques mots (quand, où, combien, son état, un souvenir) : Lucien remplit la fiche.'
          )}
        </Text>
        {isVoiceRecordingSupported() && (
          <Group gap="sm">
            {isRecording ? (
              <Button
                variant="danger"
                leftSection={<IconPlayerStopFilled size={20} />}
                onClick={stop}
              >
                {t('inventory.add.stop', 'Arrêter · {{clock}}', {
                  clock: formatClock(recordedSeconds + elapsedSeconds),
                })}
              </Button>
            ) : (
              remainingSeconds > 0 && (
                <Button
                  variant="outline"
                  leftSection={<IconMicrophone size={20} />}
                  onClick={startRecording}
                >
                  {recordings.length
                    ? t('inventory.add.recordMore', 'Continuer à raconter')
                    : t('inventory.add.record', 'Raconter à la voix')}
                </Button>
              )
            )}
            {recordings.length > 0 && !isRecording && (
              <Button variant="outline" onClick={() => setRecordings([])}>
                {t('inventory.add.recordAgain', 'Tout recommencer')}
              </Button>
            )}
          </Group>
        )}
        {recordings.length > 0 && !isRecording && (
          <Text size="sm">
            {recordings.length > 1
              ? t('inventory.add.recordedParts', 'Enregistré · {{clock}} en {{count}} fois', {
                  clock: formatClock(recordedSeconds),
                  count: recordings.length,
                })
              : t('inventory.add.recorded', 'Enregistré · {{clock}}', {
                  clock: formatClock(recordedSeconds),
                })}
            {remainingSeconds === 0 &&
              ` · ${t('inventory.add.voiceFull', 'Les 3 minutes sont utilisées.')}`}
          </Text>
        )}
        <div>
          <Button
            variant="light"
            leftSection={<IconSparkles size={20} />}
            loading={isDrafting}
            onClick={askLucien}
          >
            {t('inventory.add.askLucien', 'Remplir la fiche avec Lucien')}
          </Button>
        </div>
        {lucienHint && <Alert color="orange">{lucienHint}</Alert>}
        {draftError && <Alert color="gray">{draftError}</Alert>}

        {draft?.unknownRoomName && (
          <Alert color="teal">
            <div className={styles.section}>
              <span>
                {t(
                  'inventory.add.unknownRoom',
                  'Vous avez dit « {{name}} », qui n’existe pas encore dans la maison.',
                  { name: draft.unknownRoomName }
                )}
              </span>
              <div>
                <Button variant="outline" loading={isCreatingRoom} onClick={createSpokenRoom}>
                  {t('inventory.add.createRoom', 'Créer la pièce {{name}}', {
                    name: draft.unknownRoomName,
                  })}
                </Button>
              </div>
            </div>
          </Alert>
        )}

        {draft?.suggestedReplacementValue &&
          !webPriceAnswered &&
          values.replacementValueSource !== InventoryValueSource.Manual && (
            <Alert color="teal" title={t('inventory.add.lookalike', 'Ça ressemble à')}>
              <div className={styles.section}>
                <span>
                  {draft.suggestedReplacementValueLabel}
                  {' · '}
                  {t('inventory.add.newToday', 'Neuf aujourd’hui : {{price}}', {
                    price: formatEuros(draft.suggestedReplacementValue),
                  })}
                  {draft.suggestedReplacementValueUrl && (
                    <>
                      {' · '}
                      <a href={draft.suggestedReplacementValueUrl} target="_blank" rel="noreferrer">
                        {t('inventory.add.source', 'voir la page')}
                      </a>
                    </>
                  )}
                </span>
                <Group gap="sm">
                  <Button variant="outline" onClick={() => setWebPriceAnswered(true)}>
                    {t('inventory.add.notThisOne', 'Non')}
                  </Button>
                  <Button onClick={acceptWebPrice}>
                    {t('inventory.add.keepPrice', 'Garder ce prix')}
                  </Button>
                </Group>
              </div>
            </Alert>
          )}
      </div>
    </Fieldset>
  );

  const hasSuggestions = taskTitle !== null || memoryTitle !== null || otherSuggestions.length > 0;
  const suggestionsSection = hasSuggestions ? (
    <Fieldset legend={t('inventory.add.lucienProposes', 'Lucien vous propose aussi')}>
      <div className={styles.section}>
        {memoryTitle !== null && (
          <>
            <Checkbox
              checked={memoryChecked}
              onChange={event => setMemoryChecked(event.currentTarget.checked)}
              label={
                recordings.length
                  ? t(
                      'inventory.add.memoryWithVoice',
                      'Ajouter un souvenir à la Mémoire de la maison (votre voix est gardée avec)'
                    )
                  : t('inventory.add.memory', 'Ajouter un souvenir à la Mémoire de la maison')
              }
            />
            {memoryChecked && (
              <TextInput
                label={t('inventory.add.memoryTitle', 'Titre du souvenir')}
                value={memoryTitle}
                onChange={event => setMemoryTitle(event.currentTarget.value)}
              />
            )}
            {memoryChecked && toldDate && (
              <Radio.Group
                label={t('inventory.add.memoryWhen', 'Ce souvenir date de quand ?')}
                value={useToldDate ? 'told' : 'today'}
                onChange={value => setUseToldDate(value === 'told')}
              >
                <Group gap="md" mt="xs">
                  <Radio value="told" label={toldDate} />
                  <Radio value="today" label={t('inventory.add.memoryToday', 'Aujourd’hui')} />
                </Group>
              </Radio.Group>
            )}
          </>
        )}
        {taskTitle !== null && (
          <>
            <Checkbox
              checked={taskChecked}
              onChange={event => setTaskChecked(event.currentTarget.checked)}
              label={t('inventory.add.task', 'Créer une action, pour vous, sans date')}
            />
            {taskChecked && (
              <TextInput
                label={t('inventory.add.taskTitle', 'Titre de l’action')}
                value={taskTitle}
                onChange={event => setTaskTitle(event.currentTarget.value)}
              />
            )}
          </>
        )}
        {otherSuggestions.map(link => (
          <Checkbox
            key={suggestionKey(link.type, link.id)}
            checked={checked.has(suggestionKey(link.type, link.id))}
            onChange={() => toggle(suggestionKey(link.type, link.id))}
            label={
              link.type === LinkableEntityType.Expense
                ? t('inventory.add.linkExpense', 'Relier la dépense « {{title}} »', {
                    title: link.title,
                  })
                : t('inventory.add.linkContact', 'Relier le contact « {{title}} »', {
                    title: link.title,
                  })
            }
          />
        ))}
      </div>
    </Fieldset>
  ) : undefined;

  const invoiceSuggestions =
    documentSuggestions.length > 0 ? (
      <>
        <Text size="sm" c="dimmed">
          {t('inventory.add.inDocuments', 'Dans vos documents, seuls ceux cochés seront reliés :')}
        </Text>
        {documentSuggestions.map(link => (
          <Checkbox
            key={suggestionKey(link.type, link.id)}
            checked={checked.has(suggestionKey(link.type, link.id))}
            onChange={() => toggle(suggestionKey(link.type, link.id))}
            label={
              link.reason
                ? t('inventory.add.foundWords', '{{title}} (contient « {{words}} »)', {
                    title: link.title,
                    words: link.reason,
                  })
                : link.title
            }
          />
        ))}
      </>
    ) : undefined;

  return (
    <InventoryItemForm
      values={values}
      onChange={onChange}
      photos={photos}
      onPhotosChange={setPhotos}
      onPhotosUploadingChange={setPhotosUploading}
      invoices={invoices}
      onInvoicesChange={setInvoices}
      roomOptions={roomOptions}
      valuesVisible
      rule={rule}
      sources={draft?.sources}
      errors={errors}
      lucienSection={lucienSection}
      suggestionsSection={suggestionsSection}
      invoiceSuggestions={invoiceSuggestions}
      onSubmit={() => save(false)}
      onCancel={onClose}
      isLoading={isSaving}
      submitLabel={t('common.save', 'Enregistrer')}
      submitAndAddAnotherLabel={t(
        'inventory.add.saveAndAddAnother',
        'Enregistrer et ajouter un autre'
      )}
      onSubmitAndAddAnother={() => save(true)}
    />
  );
};

export default InventoryItemCreationFormWrapper;
